// Custom uniqueId implementation
let idCounter = 0;
const uniqueId = (prefix = "") => `${prefix}${++idCounter}`;

import { useRef } from "react";
import type { LegendType } from "recharts";
import { getTextWidth } from "senf-shared/utils/helpers";
import { cn } from "../../../util/classNameConstructor";
import Icon from "../../core/icon/Icon";

export type LegendPayload = {
  label: string;
  color: string;
  opacity: number;
  key?: string;
  notSortable?: boolean;
  fill?: string;
  stroke?: string;
  uid?: string;
  value?: number;
  initialOrder?: number;
  legendReference?: string;
};

export const LegendItem = ({
  type,
  color,
  opacity,
  label,
  legendReference,
  ...props
}: Partial<LegendPayload> & { type?: LegendType }) => {
  return (
    <li
      className="flex items-baseline gap-2 relative z-50"
      title={label}
      {...props}
    >
      <div
        style={{ color, opacity }}
        className={cn(
          "shrink-0 translate-y-[1px] [.html2canvas_&]:translate-y-1 bg-current rounded-full",
          type === "line" && "w-4 h-0.5 self-center mb-1",
          type !== "line" && "w-3 h-3",
        )}
      />
      <div className="flex gap-1">
        {legendReference ? (
          <span className="text-text-subdued body-sm-bold leading-tight">
            {legendReference}
          </span>
        ) : null}
        <span className="text-text-subdued body-sm shrink break-word hyphens-auto leading-tight">
          {label}
        </span>
      </div>
    </li>
  );
};

export const Legend = ({
  payload,
  type,
  className,
}: {
  payload: LegendPayload[];
  type?: LegendType;
  className?: string;
}) => {
  const sortedItems = payload?.sort(
    (a, b) =>
      (a?.initialOrder ?? a.value ?? 0) - (b?.initialOrder ?? b.value ?? 0),
  );
  const longestLabel = sortedItems?.reduce(
    (acc, { legendReference, label, value }) => {
      const title = (label || value)?.toString() || "";
      const reference = legendReference ? `${legendReference}:` : "";
      const width = getTextWidth(reference + title, "14px Nunito");
      return Math.max(width + 32, acc);
    },
    30,
  );
  const container = useRef<HTMLUListElement>(null);
  const containerWidth = container.current?.parentElement?.offsetWidth ?? 640;

  return (
    <ul
      className={cn(
        // keep the translate-y-0 to enforce the z-index
        "mx-auto flex list-none flex-wrap justify-center gap-x-3 gap-y-1 !pl-0 translate-y-0",
        containerWidth < longestLabel
          ? "max-w-[600px] justify-start flex-col"
          : "",
        className,
      )}
      ref={container}
    >
      {sortedItems?.map((entry, index) => {
        const { legendReference, label } = entry;
        const title = label || entry.value;
        return (
          <LegendItem
            key={uniqueId(`legend-item-${index}`)}
            color={entry.fill || entry.color}
            opacity={entry.opacity}
            label={title?.toString()}
            legendReference={
              legendReference ? `${legendReference}:` : undefined
            }
            type={type}
          />
        );
      })}
    </ul>
  );
};
